import * as React from 'react';

import {
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowUp,
  Check,
  FileText,
  Mic,
  Paperclip,
  Square,
  X,
} from 'lucide-react';

import { AURA_TOUR_ANCHORS } from '@modules/_shared/components/chat/auraTour';
import {
  ACCEPTED_FILE_TYPES,
  type ChatFileUIPart,
} from '@modules/_shared/components/chat/chatAttachments';
import {
  chatFetch,
  getChatHeaders,
} from '@modules/_shared/components/chat/chatFetch';
import { ChatImagePreviewDialog } from '@modules/_shared/components/chat/ChatImagePreviewDialog';
import { DictationWaveform } from '@modules/_shared/components/chat/DictationWaveform';
import { formatElapsed } from '@modules/_shared/components/chat/dictationWaveformHelpers';
import {
  FALLBACK_TRANSCRIBE_MODELS,
  MAX_AUDIO_BYTES,
  TRANSCRIBE_URL,
  type TranscribeModelOption,
  clearStoredTranscribeModelId,
  parseTranscribeModelsBody,
  readStoredTranscribeModelId,
  resolveTranscribeModelId,
  transcribeModelsUrl,
  writeStoredTranscribeModelId,
} from '@modules/_shared/components/chat/transcribeDictation';
import {
  type DictationError,
  useDictation,
} from '@modules/_shared/components/chat/useDictation';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

const dictationErrorText = (error: DictationError, t: Translate): string => {
  switch (error.kind) {
    case 'permission':
      return t('Allow the microphone to dictate.');
    case 'too-large':
      return t('Recording is too large ({{bytes}} bytes, max {{max}}).', {
        bytes: error.bytes,
        max: MAX_AUDIO_BYTES,
      });
    case 'read-failed':
      return t('Could not read the recording.');
    case 'not-json':
      return t('Transcription response was not JSON.');
    case 'no-text':
      return t('Transcription response had no text.');
    case 'failed':
      return t('Transcription failed ({{status}}).', {
        status: error.status,
      });
    case 'message':
      return error.text;
  }
};

const ElapsedTime = ({ startedAt }: { startedAt: number }) => {
  const { colors } = useTheme();
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(interval);
  }, []);
  return (
    <span
      css={{
        fontSize: 12,
        color: colors.grey600,
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      {formatElapsed(now - startedAt)}
    </span>
  );
};

type Props = {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  isLoading: boolean;
  modelsAvailable?: boolean;
  footer?: React.ReactNode;
  files: ChatFileUIPart[];
  onFilesChange: (files: ChatFileUIPart[]) => void;
  onAddFiles: (files: File[]) => void;
};

export type ChatInputHandle = {
  focus: () => void;
};

const AttachmentPreview = ({
  file,
  onRemove,
  onPreview,
}: {
  file: ChatFileUIPart;
  onRemove: () => void;
  onPreview: () => void;
}) => {
  const { colors } = useTheme();
  const { t } = useLocale();
  const isImage = file.mediaType.startsWith('image/');
  return (
    <div
      css={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        padding: isImage ? 0 : '5px 8px',
        backgroundColor: colors.grey100,
        overflow: 'visible',
      }}
    >
      {isImage ? (
        <img
          src={file.url}
          alt={file.filename ?? 'attachment'}
          onClick={onPreview}
          css={{
            width: 44,
            height: 44,
            objectFit: 'cover',
            borderRadius: 8,
            display: 'block',
            cursor: 'zoom-in',
          }}
        />
      ) : (
        <>
          <FileText size={14} color={colors.grey600} />
          <span
            css={{
              fontSize: '0.7rem',
              color: colors.grey700,
              maxWidth: 110,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {file.filename ?? 'document'}
          </span>
        </>
      )}
      <IconButton
        size="small"
        title={t('Remove attachment')}
        onClick={onRemove}
        sx={{
          position: 'absolute',
          top: -7,
          right: -7,
          width: 16,
          height: 16,
          backgroundColor: colors.grey700,
          color: colors.white,
          '&:hover': { backgroundColor: colors.grey900 },
        }}
      >
        <X size={10} />
      </IconButton>
    </div>
  );
};

export const ChatInput = React.forwardRef<ChatInputHandle, Props>(
  (
    {
      input,
      onInputChange,
      onSubmit,
      onStop,
      isLoading,
      modelsAvailable = true,
      footer,
      files,
      onFilesChange,
      onAddFiles,
    },
    ref,
  ) => {
    const { t } = useLocale();
    const { colors } = useTheme();
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const [transcribeMenuPosition, setTranscribeMenuPosition] = React.useState<{
      top: number;
      left: number;
    } | null>(null);
    const [transcribeModels, setTranscribeModels] = React.useState<
      TranscribeModelOption[]
    >(FALLBACK_TRANSCRIBE_MODELS);
    const [transcribeModelId, setTranscribeModelId] = React.useState<
      string | null
    >(() => readStoredTranscribeModelId() ?? FALLBACK_TRANSCRIBE_MODELS[0].id);
    const transcribeModelIdRef = React.useRef(transcribeModelId);
    transcribeModelIdRef.current = transcribeModelId;
    const catalogAbortRef = React.useRef<AbortController | null>(null);
    const canSend =
      (input.trim().length > 0 || files.length > 0) &&
      !isLoading &&
      modelsAvailable;

    React.useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    // Voice dictation appends final transcript chunks to the input. Read the
    // latest input through a ref so the (stable) onTranscript callback never
    // works with a stale value.
    const inputRef = React.useRef(input);
    inputRef.current = input;
    const onInputChangeRef = React.useRef(onInputChange);
    onInputChangeRef.current = onInputChange;
    React.useEffect(() => () => catalogAbortRef.current?.abort(), []);
    const loadTranscribeCatalog = React.useCallback(() => {
      if (catalogAbortRef.current != null) {
        return;
      }
      const controller = new AbortController();
      catalogAbortRef.current = controller;
      chatFetch(transcribeModelsUrl(TRANSCRIBE_URL), {
        headers: getChatHeaders(),
        signal: controller.signal,
      })
        .then(async response => {
          const raw = await response.text();
          let payload: unknown = { error: raw };
          if (raw.length > 0) {
            try {
              payload = JSON.parse(raw) as unknown;
            } catch {
              payload = { error: raw };
            }
          }
          const parsed = parseTranscribeModelsBody(payload);
          if (!response.ok && parsed.models.length === 0) {
            throw new Error(
              parsed.error ??
                `Failed to load transcription models (${response.status})`,
            );
          }
          return {
            models:
              parsed.models.length > 0
                ? parsed.models
                : FALLBACK_TRANSCRIBE_MODELS,
            defaultModelId:
              parsed.defaultModelId ?? FALLBACK_TRANSCRIBE_MODELS[0].id,
          };
        })
        .then(result => {
          if (controller.signal.aborted) {
            return;
          }
          setTranscribeModels(result.models);
          const kept = resolveTranscribeModelId(
            transcribeModelIdRef.current,
            result.models,
          );
          const next = kept ?? result.defaultModelId;
          writeStoredTranscribeModelId(next);
          setTranscribeModelId(next);
        })
        .catch(err => {
          if (controller.signal.aborted) {
            return;
          }
          catalogAbortRef.current = null;
          setTranscribeModels(FALLBACK_TRANSCRIBE_MODELS);
          console.warn(
            'Transcription model catalog unavailable',
            err instanceof Error ? err.message : err,
          );
        });
    }, []);

    const dictation = useDictation({
      modelId: transcribeModelId,
      onModelRejected: () => {
        clearStoredTranscribeModelId();
        setTranscribeModelId(null);
      },
      onTranscript: text => {
        const current = inputRef.current;
        const separator =
          current.length === 0 || current.endsWith(' ') ? '' : ' ';
        onInputChangeRef.current(`${current}${separator}${text.trim()}`);
      },
    });

    // Sending stops dictation, which would abort an in-flight transcription
    // and drop the recording.
    const canSubmit = canSend && !dictation.isTranscribing;

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (canSubmit) {
          dictation.stop();
          onSubmit();
        }
      }
    };

    const confirmDictationRef = React.useRef<HTMLButtonElement>(null);
    const dictationActive = dictation.isListening || dictation.isTranscribing;
    const wasDictationActiveRef = React.useRef(false);
    React.useEffect(() => {
      if (dictation.isListening) {
        confirmDictationRef.current?.focus();
      } else if (!dictationActive && wasDictationActiveRef.current) {
        textareaRef.current?.focus();
      }
      wasDictationActiveRef.current = dictationActive;
    }, [dictation.isListening, dictationActive]);

    const handleDictationKeyDown = (e: React.KeyboardEvent) => {
      if (!dictation.isListening) {
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        dictation.cancel();
      } else if (
        e.key === 'Enter' &&
        !(e.target instanceof HTMLButtonElement)
      ) {
        // A focused Cancel or tick button keeps its own Enter action.
        // preventDefault here would turn Enter on Cancel into a transcribe.
        e.preventDefault();
        dictation.finish();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      const pasted = Array.from(e.clipboardData?.files ?? []);
      if (pasted.length > 0) {
        e.preventDefault();
        onAddFiles(pasted);
      }
    };

    const showActive = canSubmit || isLoading;

    return (
      <div
        onKeyDown={handleDictationKeyDown}
        css={{
          padding: '0 12px calc(env(safe-area-inset-bottom, 0px) + 8px)',
          flexShrink: 0,
        }}
      >
        <div
          data-tour={AURA_TOUR_ANCHORS.composer}
          css={{
            border: `1px solid ${colors.divider}`,
            borderRadius: 12,
            backgroundColor: colors.white,
          }}
        >
          {files.length > 0 && (
            <div
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                padding: '12px 14px 0',
              }}
            >
              {files.map((file, index) => (
                <AttachmentPreview
                  key={`${file.filename ?? 'file'}-${index}`}
                  file={file}
                  onRemove={() =>
                    onFilesChange(files.filter((_, i) => i !== index))
                  }
                  onPreview={() => setPreviewUrl(file.url)}
                />
              ))}
            </div>
          )}
          <div css={{ padding: '10px 14px 0' }}>
            {dictation.isListening && dictation.stream ? (
              <div
                role="group"
                aria-label={t('Listening…')}
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  margin: '-2px -8px -2px 0',
                }}
              >
                <div css={{ flex: 1, minWidth: 0 }}>
                  <DictationWaveform
                    stream={dictation.stream}
                    color={colors.grey900}
                    baselineColor={colors.grey400}
                  />
                </div>
                {dictation.startedAt != null && (
                  <ElapsedTime startedAt={dictation.startedAt} />
                )}
                <IconButton
                  size="small"
                  title={t('Cancel')}
                  aria-label={t('Cancel')}
                  onClick={dictation.cancel}
                  sx={{ width: 28, height: 28 }}
                >
                  <X size={16} color={colors.grey600} />
                </IconButton>
                <IconButton
                  ref={confirmDictationRef}
                  size="small"
                  title={t('Done')}
                  aria-label={t('Done')}
                  onClick={dictation.finish}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: colors.primaryMain,
                    color: colors.white,
                    '&:hover': { backgroundColor: colors.primaryDark },
                  }}
                >
                  <Check size={16} strokeWidth={2.5} />
                </IconButton>
              </div>
            ) : (
              <InputBase
                inputRef={textareaRef}
                multiline
                minRows={1}
                maxRows={6}
                placeholder={
                  dictation.isTranscribing
                    ? t('Transcribing…')
                    : dictation.isListening
                    ? t('Listening…')
                    : t('Send a message...')
                }
                value={input}
                onChange={e => onInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                inputProps={{
                  enterKeyHint: 'send',
                  autoComplete: 'off',
                  autoCorrect: 'off',
                  autoCapitalize: 'sentences',
                  spellCheck: false,
                }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    p: 0,
                    '&::placeholder': { color: colors.grey400 },
                  },
                }}
              />
            )}
          </div>

          {/* Bottom row, Cursor-composer style: model selection on the left;
              attach, dictate and send on the right. */}
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '4px 6px 8px 8px',
            }}
          >
            <div css={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              {footer}
            </div>
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                hidden
                onChange={e => {
                  const selected = Array.from(e.target.files ?? []);
                  if (selected.length > 0) {
                    onAddFiles(selected);
                  }
                  e.target.value = '';
                }}
              />
              {/* Deliberately enabled while streaming: attachments only live
                  in `files` state until submit, and submit stays gated on
                  !isLoading, so queuing files mid-stream is safe. */}
              <IconButton
                size="small"
                title={t('Attach files')}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={16} color={colors.grey600} />
              </IconButton>
              {dictation.isSupported && (
                <>
                  {!dictation.isListening && (
                    <IconButton
                      size="small"
                      title={
                        dictation.isTranscribing
                          ? t('Transcribing…')
                          : t('Dictate')
                      }
                      onClick={() => void dictation.start()}
                      onContextMenu={event => {
                        event.preventDefault();
                        loadTranscribeCatalog();
                        if (transcribeModels.length === 0) {
                          return;
                        }
                        setTranscribeMenuPosition({
                          top: event.clientY,
                          left: event.clientX,
                        });
                      }}
                      disabled={dictation.isTranscribing}
                    >
                      {dictation.isTranscribing ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Mic size={16} color={colors.grey600} />
                      )}
                    </IconButton>
                  )}
                  <Menu
                    open={transcribeMenuPosition != null}
                    onClose={() => setTranscribeMenuPosition(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      transcribeMenuPosition ?? { top: 0, left: 0 }
                    }
                  >
                    {transcribeModels.map(model => (
                      <MenuItem
                        key={model.id}
                        selected={model.id === transcribeModelId}
                        onClick={() => {
                          writeStoredTranscribeModelId(model.id);
                          setTranscribeModelId(model.id);
                          setTranscribeMenuPosition(null);
                        }}
                        sx={{ gap: 1, fontSize: 13 }}
                      >
                        <Check
                          size={14}
                          color={
                            model.id === transcribeModelId
                              ? colors.primaryMain
                              : 'transparent'
                          }
                        />
                        {model.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              {(!dictation.isListening || isLoading) && (
                <IconButton
                  size="small"
                  onClick={
                    isLoading
                      ? onStop
                      : () => {
                          dictation.stop();
                          onSubmit();
                        }
                  }
                  disabled={!showActive}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: showActive
                      ? colors.primaryMain
                      : colors.grey300,
                    color: showActive ? colors.white : colors.grey500,
                    transition: 'background-color 0.15s, color 0.15s',
                    '&:hover': showActive
                      ? { backgroundColor: colors.primaryDark }
                      : {},
                    '&.Mui-disabled': {
                      backgroundColor: colors.grey300,
                      color: colors.grey500,
                    },
                  }}
                >
                  {isLoading ? (
                    <Square size={12} fill="currentColor" />
                  ) : (
                    <ArrowUp size={16} strokeWidth={2.5} />
                  )}
                </IconButton>
              )}
            </div>
          </div>
          {dictation.error && (
            <div
              css={{
                padding: '0 14px 8px',
                color: colors.errorText,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
              }}
            >
              {dictationErrorText(dictation.error, t)}
            </div>
          )}
        </div>
        <ChatImagePreviewDialog
          src={previewUrl}
          onClose={() => setPreviewUrl(null)}
        />
      </div>
    );
  },
);
